import React, { useState, useCallback, useRef } from 'react';
import {
  Upload,
  CheckCircle,
  AlertCircle,
  X,
  FileText,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  FolderSelector,
  type SelectedFolder
} from './FolderSelector';
import { MachineMultiSelect } from '@/components/shared/MachineMultiSelect';
import { getMachineLabel, type MachineCode } from '@/constants/machines';
import { useDocumentUpload } from '@/hooks/useDocumentUpload';
import { useAPIClient } from '@/hooks/useAPIClient';
import { CheckExistingRequest } from '@/types/api';
import { useToast } from '@/hooks/use-toast';

interface FileItem {
  file: File;
  id: string;
  machines: MachineCode[]; // Optional machine assignments per file (supports multiple)
}

interface UploadProgress {
  fileId: string;
  filename: string;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

interface UploadDialogProps {
  trigger: React.ReactNode;
  onUploadComplete?: () => void;
}

const SUPPORTED_EXTENSIONS = ['.pdf', '.docx', '.pptx', '.txt'];
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

export const UploadDialog: React.FC<UploadDialogProps> = ({
  trigger,
  onUploadComplete
}) => {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<'folder' | 'files' | 'review' | 'upload'>('folder');
  const [selectedFolder, setSelectedFolder] = useState<SelectedFolder | null>(null);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [existingFiles, setExistingFiles] = useState<string[]>([]);
  const [baseNameConflicts, setBaseNameConflicts] = useState<Array<{
    uploading_file: string;
    conflicting_files: string[];
  }>>([]);
  const [crossFolderConflicts, setCrossFolderConflicts] = useState<Array<{
    uploading_file: string;
    conflicting_files: string[];
  }>>([]);
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
  const [isCheckingFiles, setIsCheckingFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadDocuments, isUploading, uploadError } = useDocumentUpload();
  const apiClient = useAPIClient();

  const validateFile = (file: File): string | null => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!SUPPORTED_EXTENSIONS.includes(extension)) {
      return `Unsupported file type: ${extension}. Supported: ${SUPPORTED_EXTENSIONS.join(', ')}`;
    }

    if (file.size > MAX_FILE_SIZE) {
      return `File too large: ${Math.round(file.size / (1024 * 1024))}MB. Maximum: 100MB`;
    }

    return null;
  };

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const newFiles: FileItem[] = [];
    const errors: string[] = [];

    Array.from(selectedFiles).forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        const isDuplicate = files.some(f =>
          f.file.name === file.name && f.file.size === file.size
        );

        if (!isDuplicate) {
          newFiles.push({
            file,
            id: `${file.name}_${Date.now()}_${Math.random()}`,
            machines: []
          });
        }
      }
    });

    if (newFiles.length > 0) {
      setFiles(prev => [...prev, ...newFiles]);
    }

    // Show errors if any
    if (errors.length > 0) {
      console.error('File validation errors:', errors);
      // You could show these errors in a toast or error dialog
    }
  }, [files]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
  }, [handleFileSelect]);

  const removeFile = (fileId: string) => {
    setFiles(files => files.filter(f => f.id !== fileId));
  };

  const updateFileMachines = (fileId: string, machines: MachineCode[]) => {
    setFiles(files => files.map(f =>
      f.id === fileId ? { ...f, machines } : f
    ));
  };

  const canProceedToFiles = selectedFolder !== null;
  const canProceedToReview = files.length > 0;
  const canUpload = selectedFolder && files.length > 0;

  const resetDialog = () => {
    setCurrentStep('folder');
    setSelectedFolder(null);
    setFiles([]);
    setUploadProgress([]);
    setExistingFiles([]);
    setBaseNameConflicts([]);
    setCrossFolderConflicts([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!canUpload || !selectedFolder) return;

    // First check for existing files and base name conflicts
    const result = await checkForExistingFiles();

    const hasExactMatches = result.existing_files.length > 0;
    const hasBaseConflicts = result.base_name_conflicts.length > 0;
    const hasCrossFolderConflicts = result.cross_folder_conflicts.length > 0;

    if (hasExactMatches || hasBaseConflicts || hasCrossFolderConflicts) {
      // Show confirmation dialog with conflict details
      setExistingFiles(result.existing_files);
      setBaseNameConflicts(result.base_name_conflicts);
      setCrossFolderConflicts(result.cross_folder_conflicts);
      setShowOverwriteConfirm(true);
    } else {
      // No conflicts, proceed directly
      proceedWithUpload();
    }
  };

  const checkForExistingFiles = async () => {
    if (!selectedFolder) return { existing_files: [], base_name_conflicts: [], cross_folder_conflicts: [] };

    setIsCheckingFiles(true);
    try {
      const request: CheckExistingRequest = {
        filenames: files.map(f => f.file.name),
        module: selectedFolder.module,
        subpath: selectedFolder.subpath,
      };

      const response = await apiClient.upload.checkExisting(request);
      return {
        existing_files: response.existing_files,
        base_name_conflicts: response.base_name_conflicts,
        cross_folder_conflicts: response.cross_folder_conflicts || []
      };
    } catch (error) {
      console.error('Failed to check existing files:', error);

      // Show user-visible error notification
      toast({
        title: 'Konfliktprüfung fehlgeschlagen',
        description: 'Die Prüfung auf existierende Dateien ist fehlgeschlagen. Sie können trotzdem fortfahren, aber möglicherweise werden Dateien überschrieben.',
        variant: 'destructive',
      });

      return { existing_files: [], base_name_conflicts: [], cross_folder_conflicts: [] };
    } finally {
      setIsCheckingFiles(false);
    }
  };

  const proceedWithUpload = async () => {
    if (!selectedFolder) return;

    setCurrentStep('upload');

    // Initialize progress tracking
    const initialProgress = files.map(f => ({
      fileId: f.id,
      filename: f.file.name,
      progress: 0,
      status: 'pending' as const
    }));
    setUploadProgress(initialProgress);

    try {
      // Update all files to uploading
      setUploadProgress(prev => prev.map(p => ({ ...p, status: 'uploading', progress: 50 })));

      // Build file_machines map from files with explicit machine assignments (supports multiple)
      const fileMachines: Record<string, string[]> = {};
      files.forEach(f => {
        if (f.machines && f.machines.length > 0) {
          fileMachines[f.file.name] = f.machines;
        }
      });

      // Make upload request
      const uploadOptions = {
        module: selectedFolder.module,
        subpath: selectedFolder.subpath,
        file_machines: Object.keys(fileMachines).length > 0 ? fileMachines : undefined,
      };

      const result = await uploadDocuments({
        files: files.map(f => f.file),
        options: uploadOptions
      });

      // Mark successful files as complete
      setUploadProgress(prev => prev.map(p => {
        const uploadedFile = result.uploaded_files.find(uf =>
          uf.filename === p.filename
        );
        const failedFile = result.failed_files.find(ff =>
          ff.filename === p.filename
        );

        if (uploadedFile) {
          return { ...p, status: 'success', progress: 100 };
        } else if (failedFile) {
          return { ...p, status: 'error', progress: 0, error: failedFile.error };
        }
        return p;
      }));

      // Call completion callback
      if (onUploadComplete) {
        onUploadComplete();
      }

      // Close after short delay if all succeeded
      if (result.failed === 0) {
        setTimeout(() => {
          setIsOpen(false);
          resetDialog();
        }, 1500);
      }

    } catch (error) {
      console.error('Upload failed:', error);
      console.error('Upload context:', {
        selectedFolder,
        filesCount: files.length,
        canUpload,
        currentStep
      });

      setUploadProgress(prev => prev.map(p => ({
        ...p,
        status: 'error',
        error: error instanceof Error ? error.message : 'Upload failed'
      })));
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'folder':
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold">1. Ziel-Ordner auswählen</h3>
              <p className="text-sm text-muted-foreground">
                Wählen Sie den Ordner aus, in dem die Dateien gespeichert werden sollen.
                Der Ordnerpfad wird als Kontext für die KI-Verarbeitung verwendet.
              </p>
            </div>

            <FolderSelector
              selectedFolder={selectedFolder || undefined}
              onFolderSelect={setSelectedFolder}
            />

            <div className="flex justify-end">
              <Button
                onClick={() => setCurrentStep('files')}
                disabled={!canProceedToFiles}
              >
                Weiter zu Dateien
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 'files':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">2. Dateien hinzufügen</h3>
                <p className="text-sm text-muted-foreground">
                  Ziehen Sie Dateien hierher oder wählen Sie sie aus.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => setCurrentStep('folder')}
                size="sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Ordner ändern
              </Button>
            </div>

            {/* Selected folder display */}
            <Alert>
              <FileText className="h-4 w-4" />
              <AlertDescription>
                Ziel: <code className="px-1 py-0.5 bg-muted rounded text-xs">
                  input/{selectedFolder?.display_path}
                </code>
              </AlertDescription>
            </Alert>

            {/* Drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">
                Dateien hierher ziehen oder klicken zum Auswählen
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Unterstützte Formate: {SUPPORTED_EXTENSIONS.join(', ')} (max. 100MB)
              </p>
              <Button variant="outline">
                Dateien auswählen
              </Button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={SUPPORTED_EXTENSIONS.join(',')}
              onChange={handleFileInputChange}
              className="hidden"
            />

            {files.length > 0 && (
              <>
                <Separator />
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">
                      Ausgewählte Dateien ({files.length})
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Maschine optional zuweisen
                    </div>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {files.map((fileItem) => (
                      <div
                        key={fileItem.id}
                        className="flex items-center gap-2 p-2 border rounded-lg"
                      >
                        <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate" title={fileItem.file.name}>
                            {fileItem.file.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {Math.round(fileItem.file.size / 1024)}KB
                          </div>
                        </div>
                        <MachineMultiSelect
                          value={fileItem.machines}
                          onChange={(machines) => updateFileMachines(fileItem.id, machines)}
                          placeholder="Auto"
                          className="w-40 flex-shrink-0"
                        />
                        <Badge variant="secondary" className="text-xs flex-shrink-0">
                          {fileItem.file.name.split('.').pop()?.toUpperCase() || 'FILE'}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-8 h-8 p-0 text-muted-foreground hover:text-destructive flex-shrink-0"
                          onClick={() => removeFile(fileItem.id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setCurrentStep('folder')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück
              </Button>
              <Button
                onClick={() => setCurrentStep('review')}
                disabled={!canProceedToReview}
              >
                Zur Übersicht
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 'review':
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold">3. Upload-Übersicht</h3>
              <p className="text-sm text-muted-foreground">
                Überprüfen Sie die Einstellungen vor dem Upload.
              </p>
            </div>

            <div className="space-y-3">
              <div className="p-3 bg-muted/20 rounded-lg">
                <div className="text-sm font-medium mb-1">Ziel-Ordner</div>
                <code className="text-xs bg-background px-2 py-1 rounded">
                  input/{selectedFolder?.display_path}
                </code>
              </div>

              <div className="p-3 bg-muted/20 rounded-lg">
                <div className="text-sm font-medium mb-2">
                  Dateien ({files.length})
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {files.map((fileItem, index) => (
                    <div key={fileItem.id} className="flex items-center gap-2 text-sm">
                      <Badge variant="outline" className="w-8 text-xs">
                        {index + 1}
                      </Badge>
                      <span className="flex-1 truncate">{fileItem.file.name}</span>
                      {fileItem.machines && fileItem.machines.length > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {fileItem.machines.map(m => getMachineLabel(m)).join(', ')}
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {Math.round(fileItem.file.size / 1024)}KB
                      </span>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setCurrentStep('files')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dateien ändern
              </Button>
              <Button
                onClick={handleUpload}
                disabled={isCheckingFiles}
              >
                {isCheckingFiles ? (
                  <>
                    <div className="w-4 h-4 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Prüfe Dateien...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {files.length} Dateien hochladen
                  </>
                )}
              </Button>
            </div>
          </div>
        );

      case 'upload':
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold">Upload läuft...</h3>
              <p className="text-sm text-muted-foreground">
                Dateien werden hochgeladen und für die Verarbeitung vorbereitet.
              </p>
            </div>

            <div className="space-y-3">
              {uploadProgress.map(progress => (
                <div key={progress.fileId} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {progress.status === 'success' ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : progress.status === 'error' ? (
                        <AlertCircle className="w-4 h-4 text-red-500" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                      )}
                      <span className="text-sm font-medium truncate">
                        {progress.filename}
                      </span>
                    </div>
                    <Badge variant={
                      progress.status === 'success' ? 'default' :
                      progress.status === 'error' ? 'destructive' : 'secondary'
                    }>
                      {progress.status === 'success' ? 'Erfolgreich' :
                       progress.status === 'error' ? 'Fehler' : 'Upload...'}
                    </Badge>
                  </div>
                  <Progress value={progress.progress} className="h-2" />
                  {progress.error && (
                    <p className="text-xs text-red-500">{progress.error}</p>
                  )}
                </div>
              ))}
            </div>

            {!isUploading && (
              <div className="flex justify-center">
                <Button onClick={() => setIsOpen(false)}>
                  Schließen
                </Button>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild onClick={resetDialog}>
          {trigger}
        </DialogTrigger>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dokumente hochladen</DialogTitle>
            <DialogDescription>
              Laden Sie Dokumente in den gewünschten Ordner hoch.
              Die Ordnerstruktur wird als Kontext für die KI-Verarbeitung verwendet.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            {renderStepContent()}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showOverwriteConfirm} onOpenChange={(open) => {
        setShowOverwriteConfirm(open);
        if (!open) {
          // Clear conflict states when dialog is closed
          setExistingFiles([]);
          setBaseNameConflicts([]);
          setCrossFolderConflicts([]);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(baseNameConflicts.length > 0 || crossFolderConflicts.length > 0) ? "WARNUNG: Ausgabe-Kollision" : "Dateien bereits vorhanden"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                {/* Exact filename matches */}
                {existingFiles.length > 0 && (
                  <div>
                    <p className="font-medium text-foreground">Die folgenden Dateien existieren bereits und werden überschrieben:</p>
                    <ul className="mt-2 list-disc list-inside space-y-1">
                      {existingFiles.map(filename => (
                        <li key={filename} className="text-sm font-medium text-foreground">{filename}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Base name conflicts */}
                {baseNameConflicts.length > 0 && (
                  <div className="border-l-4 border-orange-500 pl-4 bg-orange-50 dark:bg-orange-950/30 p-3 rounded">
                    <p className="font-bold text-orange-700 dark:text-orange-400 mb-2">
                      Unterschiedliche Dateiformate mit gleichem Namen gefunden
                    </p>
                    <p className="text-sm text-foreground mb-3">
                      Diese Dateien haben den gleichen Dateinamen aber unterschiedliche Formate.
                      <strong> Das Hochladen wird die verarbeiteten Ausgabedateien überschreiben.</strong>
                    </p>
                    <ul className="space-y-2">
                      {baseNameConflicts.map(conflict => (
                        <li key={conflict.uploading_file} className="text-sm text-foreground">
                          <strong>{conflict.uploading_file}</strong> → Konflikt mit{' '}
                          <span className="font-mono text-xs">
                            {conflict.conflicting_files.join(', ')}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-3">
                      HINWEIS: Alle Formate werden zu <code className="px-1 py-0.5 bg-orange-100 dark:bg-orange-900/30 rounded">.md</code> verarbeitet. Nur die zuletzt verarbeitete Datei bleibt erhalten.
                    </p>
                  </div>
                )}

                {/* Cross-folder conflicts */}
                {crossFolderConflicts.length > 0 && (
                  <div className="border-l-4 border-red-500 pl-4 bg-red-50 dark:bg-red-950/30 p-3 rounded">
                    <p className="font-bold text-red-700 dark:text-red-400 mb-2">
                      Ordner-Übergreifende Duplikate
                    </p>
                    <p className="text-sm text-foreground mb-3">
                      Diese Dateien existieren bereits in <strong>anderen Eingabeordnern</strong>:
                    </p>
                    <ul className="space-y-2">
                      {crossFolderConflicts.map(conflict => (
                        <li key={conflict.uploading_file} className="text-sm text-foreground">
                          <strong>{conflict.uploading_file}</strong> → Konflikt mit:{' '}
                          <span className="font-mono text-xs">
                            {conflict.conflicting_files.join(', ')}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-3">
                      WARNUNG: Da beide Dateien zur gleichen Ausgabedatei verarbeitet werden, wird die zuletzt verarbeitete Version die andere überschreiben!
                    </p>
                  </div>
                )}

                <p className="text-sm font-medium text-foreground">Möchten Sie fortfahren?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowOverwriteConfirm(false);
              setExistingFiles([]);
              setBaseNameConflicts([]);
              setCrossFolderConflicts([]);
            }}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowOverwriteConfirm(false);
                setExistingFiles([]);
                setBaseNameConflicts([]);
                setCrossFolderConflicts([]);
                proceedWithUpload();
              }}
              className={
                (baseNameConflicts.length > 0 || crossFolderConflicts.length > 0)
                  ? crossFolderConflicts.length > 0
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-orange-600 hover:bg-orange-700"
                  : ""
              }
            >
              {(baseNameConflicts.length > 0 || crossFolderConflicts.length > 0) ? "Trotzdem hochladen" : "Überschreiben"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};